// src/components/ErrorPage.jsx
import React, { useContext } from "react";
import {
  FaTimesCircle,
  FaQuestionCircle,
  FaUserSlash,
  FaLock,
  FaUnlink,
  FaMugHot,
  FaCopy,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../AuthProvider";

/** @param {{ message: any; status: any; onRetry?: () => void; showLogout?: boolean; onLogout?: () => void }} props */
const ErrorPage = ({ message, status, onRetry, showLogout = false, onLogout }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);

  const errorTitle = typeof message === "object" ? message?.title : null;
  const errorMessage = typeof message === "object" ? message?.message : message;

  const getStatusIcon = (statusCode) => {
    const base = "text-7xl";
    switch (statusCode) {
      case 404:
        return <FaQuestionCircle className={`text-yellow-500 dark:text-yellow-400 ${base}`} />;
      case 401:
        return <FaUserSlash className={`text-blue-500 dark:text-blue-400 ${base}`} />;
      case 403:
        return <FaLock className={`text-orange-500 dark:text-orange-400 ${base}`} />;
      case 409:
        return <FaCopy className={`text-orange-500 dark:text-orange-400 ${base}`} />;
      case 418:
        return <FaMugHot className={`text-gray-500 dark:text-gray-400 ${base}`} />;
      case 500:
      case 503:
        return <FaUnlink className={`text-gray-500 dark:text-gray-400 ${base}`} />;
      default:
        return <FaTimesCircle className={`text-red-500 dark:text-red-400 ${base}`} />;
    }
  };

  const fallbackTitle =
    status ? t(`error.${status}.title`, t("error.error_title2")) : t("error.error_title2");

  const fallbackMessage =
    status ? t(`error.${status}.message`, t("error.error_message")) : t("error.error_message");

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 px-6 py-10">
      <div className="w-full max-w-2xl bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 sm:p-8">
        <div className="flex flex-col md:flex-row items-center gap-6">
          {/* Text */}
          <div className="md:w-2/3 text-center md:text-left">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {errorTitle || fallbackTitle}
            </h2>

            {status && (
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">HTTP {status}</div>
            )}

            <p className="mt-3 text-gray-700 dark:text-gray-300">
              {errorMessage || fallbackMessage}
            </p>

            <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:gap-3">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg
                           bg-indigo-600 text-white hover:bg-indigo-700
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                           dark:bg-indigo-500 dark:hover:bg-indigo-600 dark:focus:ring-offset-gray-800"
                >
                  {t("error.error_reload_button")}
                </button>
              )}

              {showLogout && (
                <button
                  onClick={
                    onLogout ||
                    (() => {
                      auth?.logout?.({ redirectUri: window.location.origin });
                    })
                  }
                  className="mt-3 sm:mt-0 inline-flex items-center justify-center px-5 py-2.5 rounded-lg
                           bg-gray-200 text-gray-800 hover:bg-gray-300
                           focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2
                           dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:focus:ring-offset-gray-800"
                >
                  {t("error.logout_button", "Log out")}
                </button>
              )}
            </div>
          </div>

          {/* Icon */}
          <div className="md:w-1/3 flex justify-center">{getStatusIcon(status)}</div>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
